/**
 * Task Detail Page - Update status, log hours, add standup notes and artifacts
 */

import React, { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { updateTask, addCommentToEntity, updateCommentInEntity, deleteCommentFromEntity, addReactionToComment, addUserRequest, fulfillUserRequest, dismissUserRequest } from '../store';
import { addStandupNote, addNewsFeedPost } from '../services/dataService';
import { Task, TimeLog, StandupNote, Comment, Project, Organization, NewsFeedPost, ChangeLog } from '../types';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Clock, Calendar, Link as LinkIcon, Save, Plus, History, UserPlus, UserCheck, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { trackTaskChanges, updateUserChangeStats } from '../services/changeTracking';

import ChangeHistory from '../components/ChangeHistory';
import QuickActionsBar from '../components/QuickActionsBar';
import Dependencies from '../components/Dependencies';
import Subtasks from '../components/Subtasks';
import CompletionTimeProjection from '../components/CompletionTimeProjection';
import { CommentSection } from '../components/CommentSection';
import { MarkdownDescription } from '../components/MarkdownDescription';
import { RichDescriptionEditor } from '../components/RichDescriptionEditor';
import UserRequestModal from '../components/UserRequestModal';
import { UserRequestsDisplay } from '../components/UserRequestsDisplay';
import SprintAllocationManager from '../components/SprintAllocationManager';
import WorkPlanningCalendar from '../components/WorkPlanningCalendar';
import { useApp } from '../providers/AppProvider';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { generateId } from '../utils/idGenerator';
import {
  canCreateProjectTasks,
  canEditTaskParameters,
  canViewProjectFinance,
  getProjectMemberRole,
  getProjectTeamUsers,
  isExternalPartnerUser,
} from '../utils/projectAccess';
import { getPriorityColors, getStatusColors } from '../constants/colorScheme';
import { getTaskOrder } from '../utils/taskFiltering';

export default function TaskDetail() {
  const { taskId } = useParams<{ taskId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { state, setState, loading, reload } = useApp();
  const [task, setTask] = React.useState<Task | null>(null);
  const [project, setProject] = React.useState<Project | null>(null);
  const [organization, setOrganization] = React.useState<Organization | null>(null);
  
  // Form states
  const [status, setStatus] = React.useState<Task['status']>('Not Started');
  const [standupNote, setStandupNote] = React.useState('');
  const [logHours, setLogHours] = React.useState('');
  const [logDescription, setLogDescription] = React.useState('');
  const [artifactType, setArtifactType] = React.useState('code');
  const [artifactUrl, setArtifactUrl] = React.useState('');
  const [artifactDescription, setArtifactDescription] = React.useState('');
  const [selectedAssignee, setSelectedAssignee] = React.useState('');
  const [showUserRequestModal, setShowUserRequestModal] = React.useState(false);
  const [isEditingTitle, setIsEditingTitle] = React.useState(false);
  const [editingTitle, setEditingTitle] = React.useState('');
  const [isEditingDescription, setIsEditingDescription] = React.useState(false);
  const [descriptionDraft, setDescriptionDraft] = React.useState('');
  const { tasks, projects, sprints, users } = useOrganizationData(state) as {
    tasks: Task[];
    projects: Project[];
    sprints: any[];
    users: any[];
  };

  const refreshState = useCallback(async () => {
    if (!state?.currentUser?.email) return;
    await reload(state.currentUser.email);
  }, [reload, state?.currentUser?.email]);

  const applyUserChangeStats = useCallback(
    (userId: string, changes: Task['changeLogs']) => {
      if (!changes.length) return;

      setState(prev => {
        if (!prev) return prev;

        const existingUser = prev.users.find(user => user.id === userId);
        if (!existingUser) return prev;

        const updatedUser = updateUserChangeStats(existingUser, changes);
        return {
          ...prev,
          users: prev.users.map(user => (user.id === userId ? updatedUser : user)),
        };
      });
    },
    [setState]
  );

  useEffect(() => {
    if (!state) {
      setTask(null);
      setProject(null);
      setOrganization(null);
      return;
    }

    const foundTask = tasks.find(t => t.id === taskId);
    if (foundTask && !isEditingTitle) {
      setEditingTitle(foundTask.title);
    }
    if (foundTask && !isEditingDescription) {
      setDescriptionDraft(foundTask.description || '');
    }
    if (foundTask) {
      setTask(foundTask);
      setStatus(foundTask.status);
      
      // Find project and organization
      const foundProject = projects.find(p => p.id === foundTask.projectId);
      setProject(foundProject || null);

      const foundOrg = foundProject
        ? state.organizations.find(o => o.id === foundProject.organizationId)
        : null;
      setOrganization(foundOrg || null);
      return;
    }

    setTask(null);
    setProject(null);
    setOrganization(null);
  }, [taskId, projects, state, tasks]);

  if (loading || !state) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card>
          <CardContent className="p-12">
            <p className="text-muted-foreground">Loading task...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!state.currentUser) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card>
          <CardContent className="p-12">
            <p className="text-muted-foreground">Unable to load user context.</p>
            <Button onClick={() => navigate('/dashboard')} className="mt-4">
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentUser = state.currentUser;
  const currentUserId = currentUser.id;
  const currentUserName = currentUser.name;
  const taskProjectId = task?.projectId;
  const taskOrganizationId = task?.organizationId;
  const projectUsers = project ? getProjectTeamUsers(project, users, state.teams || []) : [];
  const sameProjectTasks = tasks.filter(
    (item) => item.projectId === taskProjectId && item.organizationId === taskOrganizationId
  );
  const sameOrgUsers = users.filter((user) => user.organizationId === taskOrganizationId);
  const scopedUsersForTask = projectUsers.length > 0 ? projectUsers : sameOrgUsers;
  const isCurrentUserProjectMember = !project
    ? true
    : projectUsers.some((member) => member.id === currentUserId || member.email === currentUser.email);
  const canEditTaskParams = project
    ? canEditTaskParameters(currentUser, project)
    : currentUser.role !== 'stakeholder';
  const canCreateSubtasks = project
    ? canCreateProjectTasks(currentUser, project) || canEditTaskParams
    : currentUser.role !== 'external_partner';
  const canViewFinance = project
    ? canViewProjectFinance(currentUser, project)
    : currentUser.role === 'admin' || currentUser.role === 'stakeholder' || currentUser.role === 'investor';
  const canDeleteOwnComments = !isExternalPartnerUser(currentUser, project);

  const buildIdentitySet = (value?: string | null): Set<string> => {
    const identities = new Set<string>();
    const normalized = String(value || '').trim().toLowerCase();

    if (!normalized) {
      return identities;
    }

    identities.add(normalized);

    const withoutCommonPrefix = normalized.replace(/^user[-_]/, '');
    if (withoutCommonPrefix) {
      identities.add(withoutCommonPrefix);
    }

    const compact = normalized.replace(/[^a-z0-9]/g, '');
    if (compact) {
      identities.add(compact);
    }

    if (normalized.includes('@')) {
      const localPart = normalized.split('@')[0];
      if (localPart) {
        identities.add(localPart);
        identities.add(localPart.replace(/[^a-z0-9]/g, ''));
      }
    }

    return identities;
  };

  const getUserIdentitySet = (user: { id?: string; email?: string }): Set<string> => {
    const identities = new Set<string>();
    buildIdentitySet(user.id).forEach((value) => identities.add(value));
    buildIdentitySet(user.email).forEach((value) => identities.add(value));
    return identities;
  };

  const handleUpdateStatus = async () => {
    if (!task) return;
    if (!canEditTaskParams) {
      toast.error('Stakeholders can view tasks but cannot edit task parameters.');
      return;
    }
    
    // Track changes before update
    const oldTask = { ...task };
    const nextStatus = status as Task['status'];
    const newTask = { ...task, status: nextStatus };

    const changes = trackTaskChanges(oldTask, newTask, currentUserId, currentUserName);
    
    // Add changes to task
    const updatedChangeLogs = [...(task.changeLogs || []), ...changes];
    
    await updateTask(task.id, { status: nextStatus, changeLogs: updatedChangeLogs });
    applyUserChangeStats(currentUserId, changes);
    await refreshState();
    
    toast.success('Status updated successfully');
  };

  const handleSaveTitle = async () => {
    if (!task) return;
    if (!canEditTaskParams) {
      toast.error('Stakeholders can view tasks but cannot edit task parameters.');
      setIsEditingTitle(false);
      setEditingTitle(task.title);
      return;
    }

    const nextTitle = editingTitle.trim();
    if (!nextTitle) {
      toast.error('Task title cannot be empty');
      return;
    }

    if (nextTitle === task.title) {
      setIsEditingTitle(false);
      return;
    }

    // Track changes before update
    const oldTask = { ...task };
    const newTask = { ...task, title: nextTitle };

    const changes = trackTaskChanges(oldTask, newTask, currentUserId, currentUserName);
    
    // Add changes to task
    const updatedChangeLogs = [...(task.changeLogs || []), ...changes];
    
    await updateTask(task.id, { title: nextTitle, changeLogs: updatedChangeLogs });
    applyUserChangeStats(currentUserId, changes);
    await refreshState();
    
    setIsEditingTitle(false);
    toast.success('Task title updated successfully');
  };

  const handleSaveDescription = async () => {
    if (!task) return;
    if (!canEditTaskParams) {
      toast.error('Stakeholders can view tasks but cannot edit task parameters.');
      setIsEditingDescription(false);
      return;
    }
    await updateTask(task.id, { description: descriptionDraft });
    await refreshState();
    setIsEditingDescription(false);
    toast.success('Description updated');
  };

  const handleAddStandupNote = async () => {
    if (!task || !standupNote.trim()) return;

    const note: StandupNote = {
      id: generateId('note', task.id),
      organizationId: task.organizationId,
      authorId: currentUserId,
      content: standupNote,
      createdAt: new Date().toISOString(),
      taskId: task.id,
    };

    const updatedNotes = [...(task.standupNotes || []), note];
    await updateTask(task.id, { standupNotes: updatedNotes });
    await refreshState();
    
    setStandupNote('');
    toast.success('Standup note added');
  };

  const handleLogTime = async () => {
    if (!task || !logHours || parseFloat(logHours) <= 0) return;

    const timeLog: TimeLog = {
      id: generateId('log', task.id),
      taskId: task.id,
      userId: currentUserId,
      date: new Date().toISOString(),
      hours: parseFloat(logHours),
      description: logDescription,
    };

    const updatedLogs = [...(task.timeLogs || []), timeLog];
    await updateTask(task.id, { timeLogs: updatedLogs });
    await refreshState();
    
    setLogHours('');
    setLogDescription('');
    toast.success('Time logged successfully');
  };

  const handleAddArtifact = async () => {
    if (!task || !artifactUrl.trim()) return;

    const fieldMap = {
      'code': 'verificationLinks',
      'doc': 'externalLinks',
      'video': 'externalLinks',
    };

    const field = fieldMap[artifactType as keyof typeof fieldMap] || 'externalLinks';
    const currentLinks = (task as any)[field] || [];
    const nextLink = field === 'verificationLinks'
      ? artifactUrl
      : { type: artifactType, url: artifactUrl };
    const updatedLinks = [...currentLinks, nextLink];
    
    await updateTask(task.id, { [field]: updatedLinks });
    await refreshState();
    
    setArtifactUrl('');
    setArtifactDescription('');
    toast.success('Artifact added successfully');
  };

  if (!task) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card>
          <CardContent className="p-12">
            <p className="text-muted-foreground">Task not found</p>
            <Button onClick={() => navigate('/dashboard')} className="mt-4">
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalLogged = task.timeLogs?.reduce((sum, log) => sum + log.hours, 0) || 0;
  const estimatedHours = task.estimatedHours || 0;
  const progress = estimatedHours > 0 ? (totalLogged / estimatedHours) * 100 : 0;
  const projectMemberRole = project
    ? getProjectMemberRole(project, currentUser.id, currentUser.email)
    : currentUser.role;
  const assignedIdentitySet = new Set<string>();
  (task.assigneeIds || []).forEach((assigneeId) => {
    buildIdentitySet(assigneeId).forEach((identity) => assignedIdentitySet.add(identity));
  });
  const isAssignedToMe = Array.from(getUserIdentitySet(currentUser)).some((identity) => assignedIdentitySet.has(identity));
  const assignableUsers = projectUsers.length > 0 ? projectUsers : sameOrgUsers;
  const currentAssignees = assignableUsers.filter((user) => {
    const identities = getUserIdentitySet(user);
    return Array.from(identities).some((identity) => assignedIdentitySet.has(identity));
  });
  const OBSERVER_ROLES = new Set(['stakeholder', 'investor']);
  const availableDeveloperAssignees = assignableUsers.filter((user) => {
    const userProjectRole = project ? getProjectMemberRole(project, user.id, user.email) || user.role : user.role;
    if (OBSERVER_ROLES.has(userProjectRole)) {
      return false;
    }

    const identities = getUserIdentitySet(user);
    const alreadyAssigned = Array.from(identities).some((identity) => assignedIdentitySet.has(identity));
    return !alreadyAssigned;
  });
  const canAssignOtherDevelopers = Boolean(
    currentUser.role === 'admin' || projectMemberRole === 'admin' || projectMemberRole === 'owner'
  );
  const formatHeaderDate = (value?: string) => {
    if (!value) {
      return 'Not set';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return 'Not set';
    }

    return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  const headerMetrics = [
    {
      label: 'Progress',
      value: `${Math.round(Math.max(0, progress))}%`,
      hint: `${totalLogged.toFixed(1)}h of ${estimatedHours || 0}h logged`,
    },
    {
      label: 'Assignees',
      value: currentAssignees.length > 0 ? `${currentAssignees.length}` : 'None',
      hint:
        currentAssignees.length > 0
          ? currentAssignees.slice(0, 2).map((user) => user.name).join(', ')
          : 'No developer assigned',
    },
    {
      label: 'Target',
      value: formatHeaderDate(task.targetCompletionDate || task.targetEndDate),
      hint: task.targetCompletionDate ? 'Completion target' : task.targetEndDate ? 'Planned end date' : 'No target date',
    },
    {
      label: 'Next update',
      value: formatHeaderDate(task.nextUpdateDate),
      hint: task.nextUpdateDate ? 'Expected check-in' : 'No update scheduled',
    },
  ];
  const visibleChangeLogs = (task.changeLogs || []).filter((change) => {
    if (change.changeType !== 'cost') {
      return true;
    }

    if (!canViewFinance) {
      return false;
    }

    if (currentUser.role === 'admin') {
      return true;
    }

    const changedByUser = users.find((user) => user.id === change.changedBy);
    return !isExternalPartnerUser(changedByUser || null, project);
  });
  const sanitizePlanningPokerComment = (content: string): string => {
    const isCostPlanningPokerResult =
      /\*\*Planning Poker Results\*\*/i.test(content) &&
      /\*\*Mode:\*\*\s*Cost Estimation/i.test(content);

    if (!isCostPlanningPokerResult || currentUser.role === 'admin') {
      return content;
    }

    if (currentUser.role === 'developer') {
      return '**Planning Poker Results**\n\nCost estimate details are restricted for your role.';
    }

    return content
      .replace(/•\s+([^:\n]+):\s*\$[\d,]+\s*\(Contractor\)/gi, '• $1: Hidden (Contractor bid hidden)')
      .replace(/•\s+Average:\s*\$[^\n]*/gi, '• Average: Restricted (admin visibility only)')
      .replace(/•\s+Consensus:\s*✅\s*\$[^\n]*/gi, '• Consensus: Restricted (admin visibility only)');
  };
  const visibleComments = (task.comments || []).map((comment) => ({
    ...comment,
    content: sanitizePlanningPokerComment(comment.content),
  }));

  const updateAssignees = async (nextAssigneeIds: string[], successMessage: string) => {
    const oldTask = { ...task };
    const newTask = { ...task, assigneeIds: nextAssigneeIds };
    const changes = trackTaskChanges(oldTask, newTask, currentUserId, currentUserName);
    const updatedChangeLogs = [...(task.changeLogs || []), ...changes];

    await updateTask(task.id, { assigneeIds: nextAssigneeIds, changeLogs: updatedChangeLogs });
    applyUserChangeStats(currentUserId, changes);
    await refreshState();
    toast.success(successMessage);
  };

  const handleAssignToMe = async () => {
    if (isAssignedToMe) {
      toast.info('Task is already assigned to you');
      return;
    }

    if (!isCurrentUserProjectMember) {
      toast.error('Only project team members can assign this task to themselves.');
      return;
    }

    await updateAssignees(Array.from(new Set([...(task.assigneeIds || []), currentUserId])), 'Task assigned to you');
  };

  const handleAssignDeveloper = async () => {
    if (!canAssignOtherDevelopers) {
      toast.error('Only project admins can assign developers from this view.');
      return;
    }

    if (!selectedAssignee) {
      toast.error('Select a developer to assign');
      return;
    }

    const selectedUser = availableDeveloperAssignees.find((user) => user.id === selectedAssignee);
    if (!selectedUser) {
      toast.error('Selected developer is no longer available');
      setSelectedAssignee('');
      return;
    }

    await updateAssignees(
      Array.from(new Set([...(task.assigneeIds || []), selectedUser.id])),
      `${selectedUser.name} assigned to task`
    );
    setSelectedAssignee('');
  };

  const isNestedInProjectLayout = location.pathname.startsWith('/app/projects/');
  const headerMeta = [task.type, task.phase, task.category].filter(Boolean).join(' • ');
  const taskStatusBadgeClass = `border ${getStatusColors(task.status).badge}`;
  const taskPriorityBadgeClass = getPriorityColors(task.priority || 'Medium').badge;
  const taskOrder = getTaskOrder(task);
  const headerContent = (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_340px] xl:gap-6">
      <div className="min-w-0">
        {!isNestedInProjectLayout && organization && (
          <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground sm:mb-3 sm:text-sm">
            <Building2 className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium truncate">{organization.name}</span>
            {project && (
              <>
                <span className="text-muted-foreground/70">•</span>
                <span>{project.name}</span>
              </>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => navigate(project ? `/app/projects/${project.id}` : '/dashboard')}
            size="sm"
            className="h-9 px-2 sm:px-3 text-xs sm:text-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <Badge variant="outline" className="font-mono text-xs">
            {task.id}
          </Badge>
          {taskOrder !== null && (
            <Badge variant="secondary" className="font-mono text-xs">
              #{taskOrder}
            </Badge>
          )}
          <Badge variant="outline" className={`text-xs sm:text-sm ${taskStatusBadgeClass}`}>
            {task.status}
          </Badge>
          <Badge variant="outline" className={`text-xs sm:text-sm ${taskPriorityBadgeClass}`}>
            {task.priority}
          </Badge>
        </div>

        <div className="mt-4">
          {isEditingTitle ? (
            <div className="flex gap-2">
              <input
                type="text"
                title="Task title"
                placeholder="Enter task title..."
                value={editingTitle}
                onChange={(e) => setEditingTitle(e.target.value)}
                className="flex-1 text-2xl sm:text-3xl lg:text-4xl font-bold border border-input rounded-lg px-3 py-2 bg-background text-foreground"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveTitle();
                  if (e.key === 'Escape') {
                    setIsEditingTitle(false);
                    setEditingTitle(task.title);
                  }
                }}
                autoFocus
              />
              <Button size="sm" onClick={handleSaveTitle} className="mt-2">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setIsEditingTitle(false);
                  setEditingTitle(task.title);
                }}
                className="mt-2"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold break-words flex-1">{task.title}</h1>
              {canEditTaskParams && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsEditingTitle(true)}
                  className="mt-2"
                  title="Edit task title"
                >
                  Edit
                </Button>
              )}
            </div>
          )}
          {headerMeta && (
            <p className="mt-2 text-sm text-muted-foreground sm:text-base">{headerMeta}</p>
          )}

          {/* Description */}
          <div className="mt-4">
            {isEditingDescription ? (
              <div>
                <RichDescriptionEditor
                  value={descriptionDraft}
                  onChange={setDescriptionDraft}
                  rows={5}
                  showPreview={true}
                  onSave={handleSaveDescription}
                  saveLabel="Save Description"
                  suggestionContext={{ title: task.title, type: task.type }}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIsEditingDescription(false);
                    setDescriptionDraft(task.description || '');
                  }}
                  className="mt-2"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex items-start gap-3 group/desc">
                <div className="flex-1 min-w-0">
                  {task.description ? (
                    <MarkdownDescription content={task.description} className="text-foreground" />
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No description — click Edit to add one</p>
                  )}
                </div>
                {canEditTaskParams && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsEditingDescription(true)}
                    title="Edit description"
                    className="flex-shrink-0 opacity-0 group-hover/desc:opacity-100 transition-opacity"
                  >
                    Edit
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 xl:grid-cols-4 gap-3">
          {headerMetrics.map((metric) => (
            <div key={metric.label} className="rounded-xl border border-border bg-muted/40 px-3 py-3">
              <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {metric.label}
              </div>
              <div className="mt-1 text-base font-semibold text-foreground sm:text-lg">
                {metric.value}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{metric.hint}</div>
            </div>
          ))}
        </div>
      </div>

      <Card className="border-border shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Assignment</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Current assignees</div>
            {currentAssignees.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {currentAssignees.map((user) => (
                  <div
                    key={user.id}
                    className="rounded-full border border-border bg-muted/40 px-3 py-1.5 text-sm text-foreground"
                  >
                    {user.name}
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-2 rounded-xl border border-dashed border-border bg-muted/30 px-3 py-3 text-sm text-muted-foreground">
                No assignee yet
              </div>
            )}
          </div>

          <div className="space-y-2">
            {isCurrentUserProjectMember && (
              <Button
                variant={isAssignedToMe ? 'outline' : 'default'}
                size="sm"
                onClick={handleAssignToMe}
                disabled={isAssignedToMe}
                className="h-10 w-full text-sm"
              >
                {isAssignedToMe ? (
                  <>
                    <UserCheck className="mr-2 h-4 w-4" />
                    Assigned to Me
                  </>
                ) : (
                  <>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Assign to Me
                  </>
                )}
              </Button>
            )}

            {canAssignOtherDevelopers && (
              <div className="space-y-2">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Admin assignment</div>
                <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                  <select
                    title="Assign developer"
                    value={selectedAssignee}
                    onChange={(event) => setSelectedAssignee(event.target.value)}
                    className="h-10 rounded-md border border-border bg-background px-3 text-sm text-foreground"
                  >
                    <option value="">Select team member</option>
                    {availableDeveloperAssignees.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    onClick={handleAssignDeveloper}
                    disabled={!selectedAssignee}
                    className="h-10 px-4 text-sm"
                  >
                    Assign
                  </Button>
                </div>
                {availableDeveloperAssignees.length === 0 && (
                  <p className="text-xs text-muted-foreground">All project members are already assigned.</p>
                )}
              </div>
            )}

            {!isCurrentUserProjectMember && (
              <p className="text-xs text-muted-foreground">Only project members can self-assign from this task page.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className={isNestedInProjectLayout ? 'bg-background' : 'min-h-screen bg-background'}>
      {isNestedInProjectLayout ? (
        <div className="px-4 sm:px-6 lg:px-8 pt-4 lg:pt-6 pb-3 max-w-7xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-6">
            {headerContent}
          </div>
        </div>
      ) : (
        <div className="border-border bg-card">
          <div className="px-4 sm:px-6 lg:px-8 pt-4 lg:pt-6 pb-4 max-w-7xl mx-auto">
            {headerContent}
          </div>
        </div>
      )}

      <div className="px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-3 sm:space-y-6">
            {/* Quick Actions Bar */}
            <QuickActionsBar
              onLogTime={async (hours, description) => {
                const timeLog: TimeLog = {
                  id: generateId('log', task.id),
                  taskId: task.id,
                  userId: currentUserId,
                  date: new Date().toISOString(),
                  hours,
                  description,
                };
                const updatedLogs = [...(task.timeLogs || []), timeLog];
                await updateTask(task.id, { timeLogs: updatedLogs });
                await refreshState();
              }}
              onAddNote={async (content: string) => {
                const now = new Date().toISOString();
                const quickComment: Comment = {
                  id: generateId('comment', task.id),
                  content,
                  createdBy: currentUserId,
                  createdByName: currentUserName,
                  createdAt: now,
                };
                await addCommentToEntity('task', task.id, quickComment);
                await refreshState();
              }}
              onUpdateStatus={async (newStatus: string) => {
                if (!canEditTaskParams) {
                  toast.error('Stakeholders can view tasks but cannot edit task parameters.');
                  return;
                }

                const nextStatus = newStatus as Task['status'];
                const oldTask = { ...task };
                const newTask = { ...task, status: nextStatus };
                const changes = trackTaskChanges(oldTask, newTask, currentUserId, currentUserName);
                const updatedChangeLogs = [...(task.changeLogs || []), ...changes];
                await updateTask(task.id, { status: nextStatus, changeLogs: updatedChangeLogs });
                setStatus(nextStatus);
                applyUserChangeStats(currentUserId, changes);
                // Auto-post standup note when task is completed
                if (nextStatus === 'Completed') {
                  const now = new Date().toISOString();
                  await addStandupNote({
                    id: generateId('standup', task.id),
                    organizationId: task.organizationId,
                    projectId: task.projectId,
                    authorId: currentUserId,
                    authorName: currentUserName,
                    content: `Completed task: ${task.title}`,
                    createdAt: now,
                    taskId: task.id,
                    taskTitle: task.title,
                    source: 'task_completed',
                  });
                }
                await refreshState();
              }}
              currentStatus={task.status}
              estimatedHours={estimatedHours}
            />

            {/* Dependencies */}
            <Dependencies
              task={task}
              allTasks={sameProjectTasks}
              onUpdateDependencies={async (dependencies) => {
                if (!canEditTaskParams) {
                  toast.error('Stakeholders can view tasks but cannot edit task parameters.');
                  return;
                }

                await updateTask(task.id, { dependencies });
                await refreshState();
              }}
            />

            {/* Subtasks */}
            <Subtasks
              task={task}
              allTasks={sameProjectTasks}
              users={scopedUsersForTask}
              onCreateSubtask={async (title) => {
                if (!canCreateSubtasks) {
                  toast.error('You do not have permission to create subtasks.');
                  return;
                }

                // Create a new subtask
                const newSubtaskId = `${task.id}.${(task.subtaskIds || []).length + 1}`;
                const newSubtask: Task = {
                  ...task,
                  id: newSubtaskId,
                  title,
                  description: '',
                  status: 'Not Started',
                  parentTaskIds: [task.id],
                  subtaskIds: [],
                  estimatedHours: 0,
                  timeLogs: [],
                  standupNotes: [],
                  changeLogs: [],
                  verificationLinks: [],
                  externalLinks: [],
                };

                // Update parent task with new subtask ID
                const updatedSubtaskIds = [...task.subtaskIds, newSubtaskId];
                await updateTask(task.id, { subtaskIds: updatedSubtaskIds });

                setState(prev => {
                  if (!prev) return prev;

                  return {
                    ...prev,
                    tasks: [
                      ...prev.tasks.map(existingTask =>
                        existingTask.id === task.id
                          ? { ...existingTask, subtaskIds: updatedSubtaskIds }
                          : existingTask
                      ),
                      newSubtask,
                    ],
                  };
                });
                toast.success('Subtask created');
              }}
            />

            {/* Standup Notes History */}
            {task.standupNotes && task.standupNotes.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Standup Notes History</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[...task.standupNotes].reverse().map((note) => (
                      <div key={note.id} className="border-l-2 border-primary pl-4">
                        <div className="mb-1 text-sm text-muted-foreground">
                          {new Date(note.createdAt).toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </div>
                        <p className="text-foreground">{note.content}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Time Logs History */}
            {task.timeLogs && task.timeLogs.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Time Logs</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[...task.timeLogs].reverse().map((log) => (
                      <div key={log.id} className="flex items-start justify-between border-b pb-3 last:border-0">
                        <div>
                          <div className="font-medium">{log.hours}h</div>
                          <div className="text-sm text-muted-foreground">
                            {new Date(log.date).toLocaleDateString()}
                          </div>
                          {log.description && (
                            <p className="mt-1 text-sm text-foreground">{log.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="space-y-3 sm:space-y-6">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Collaboration & Planning</h2>
                  <p className="text-sm text-muted-foreground">
                    Keep discussion, requests, and execution planning in the main reading flow.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 sm:gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <History className="w-5 h-5" />
                      Change History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ChangeHistory 
                      changes={visibleChangeLogs} 
                      users={scopedUsersForTask}
                      maxItems={10}
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between gap-3">
                      <CardTitle className="flex items-center gap-2">
                        <UserPlus className="w-5 h-5" />
                        User Requests
                      </CardTitle>
                      <Button
                        size="sm"
                        onClick={() => setShowUserRequestModal(true)}
                        className="flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Request Input
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <UserRequestsDisplay
                      requests={task.userRequests || []}
                      currentUserId={currentUserId}
                      onFulfill={async (requestId) => {
                        await fulfillUserRequest('task', task.id, requestId);
                        await refreshState();
                        toast.success('Request fulfilled');
                      }}
                      onDismiss={async (requestId) => {
                        await dismissUserRequest('task', task.id, requestId);
                        await refreshState();
                        toast.success('Request dismissed');
                      }}
                    />
                  </CardContent>
                </Card>
              </div>

              <CommentSection
                comments={visibleComments}
                currentUserId={currentUserId}
                currentUserName={currentUserName}
                canDeleteOwnComments={canDeleteOwnComments}
                enableStandupCheckbox
                enableProjectFeedCheckbox
                enableOrgFeedCheckbox
                onAddComment={async (content, parentCommentId, includeInStandup, includeInProjectFeed, includeInOrgFeed, attachments) => {
                  const now = new Date().toISOString();
                  const newComment: Comment = {
                    id: generateId('comment', task.id),
                    content,
                    createdBy: currentUserId,
                    createdByName: currentUserName,
                    createdAt: now,
                    parentCommentId,
                    attachments,
                    includeInStandup: includeInStandup || undefined,
                    includeInProjectFeed: includeInProjectFeed || undefined,
                    includeInOrgFeed: includeInOrgFeed || undefined,
                  };
                  await addCommentToEntity('task', task.id, newComment);
                  if (includeInStandup) {
                    const standupEntry: StandupNote = {
                      id: generateId('standup', task.id),
                      organizationId: task.organizationId,
                      projectId: task.projectId,
                      authorId: currentUserId,
                      authorName: currentUserName,
                      content,
                      createdAt: now,
                      taskId: task.id,
                      taskTitle: task.title,
                      source: 'comment',
                    };
                    await addStandupNote(standupEntry);
                  }
                  if (includeInProjectFeed) {
                    const feedPost: NewsFeedPost = {
                      id: generateId('nfpost', task.id),
                      organizationId: task.organizationId,
                      projectId: task.projectId,
                      scope: 'project',
                      authorId: currentUserId,
                      authorName: currentUserName,
                      content,
                      createdAt: now,
                      sourceType: 'task_comment',
                      sourceEntityId: task.id,
                      sourceEntityTitle: task.title,
                      sourceCommentId: newComment.id,
                    };
                    await addNewsFeedPost(feedPost);
                  }
                  if (includeInOrgFeed) {
                    const feedPost: NewsFeedPost = {
                      id: generateId('nfpost-org', task.id),
                      organizationId: task.organizationId,
                      scope: 'org',
                      authorId: currentUserId,
                      authorName: currentUserName,
                      content,
                      createdAt: now,
                      sourceType: 'task_comment',
                      sourceEntityId: task.id,
                      sourceEntityTitle: task.title,
                      sourceCommentId: newComment.id,
                    };
                    await addNewsFeedPost(feedPost);
                  }
                  await refreshState();
                  const extras = [includeInStandup && 'standup', includeInProjectFeed && 'project feed', includeInOrgFeed && 'org feed'].filter(Boolean);
                  toast.success(extras.length ? `Comment added & shared to ${extras.join(', ')}` : 'Comment added');
                }}
                onEditComment={async (commentId, newContent, attachments) => {
                  const updates: Partial<Comment> = { content: newContent };
                  if (attachments) {
                    updates.attachments = attachments;
                  }

                  await updateCommentInEntity('task', task.id, commentId, updates);
                  await refreshState();
                  toast.success('Comment updated');
                }}
                onDeleteComment={async (commentId) => {
                  if (!canDeleteOwnComments) {
                    toast.error('Comment deletion is restricted for your role.');
                    return;
                  }

                  await deleteCommentFromEntity('task', task.id, commentId);
                  await refreshState();
                  toast.success('Comment deleted');
                }}
                onAddReaction={async (commentId, emoji) => {
                  await addReactionToComment('task', task.id, commentId, currentUserId, emoji);
                  await refreshState();
                }}
                placeholder="Share your thoughts on this task..."
              />

              <SprintAllocationManager
                task={task}
                sprints={sprints.filter(s => s.projectId === task.projectId)}
                onUpdate={async (allocations) => {
                  if (!canEditTaskParams) {
                    toast.error('Stakeholders can view tasks but cannot edit task parameters.');
                    return;
                  }

                  let updatedChangeLogs = [...(task.changeLogs || [])];

                  const oldAllocationsStr = JSON.stringify(task.sprintAllocations || []);
                  const newAllocationsStr = JSON.stringify(allocations);
                  if (oldAllocationsStr !== newAllocationsStr) {
                    updatedChangeLogs.push({
                      id: generateId('change', task.id),
                      entityType: 'task',
                      entityId: task.id,
                      taskId: task.id,
                      changeType: 'other',
                      fieldName: 'sprintAllocations',
                      oldValue: `${(task.sprintAllocations || []).length} allocation(s)`,
                      newValue: `${allocations.length} allocation(s)`,
                      changedBy: currentUserId,
                      changedByName: currentUserName,
                      changedAt: new Date().toISOString(),
                    } as ChangeLog);
                  }

                  await updateTask(task.id, { sprintAllocations: allocations, changeLogs: updatedChangeLogs });
                  await refreshState();
                }}
              />

              <WorkPlanningCalendar
                task={task}
                currentUserId={currentUserId}
                currentUserName={currentUserName}
                onUpdate={async (workPlan) => {
                  await updateTask(task.id, { workPlan });
                  await refreshState();
                }}
              />

              {showUserRequestModal && (
                <UserRequestModal
                  users={projectUsers}
                  currentUserId={currentUserId}
                  currentUserName={currentUserName}
                  entityType="task"
                  onClose={() => setShowUserRequestModal(false)}
                  onSubmit={async (request) => {
                    await addUserRequest('task', task.id, request);
                    await refreshState();
                    toast.success('Request added');
                  }}
                />
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-3 sm:space-y-6 lg:sticky lg:top-24 self-start">
            {/* Completion Time Projection */}
            {task.estimatedHours && task.estimatedHours > 0 && (
              <CompletionTimeProjection
                task={task}
                allTasks={sameProjectTasks}
                users={scopedUsersForTask}
              />
            )}

            {/* Time Tracking Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Time Tracking</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium text-foreground">{progress.toFixed(0)}%</span>
                  </div>
                  <progress
                    value={Math.max(0, Math.min(progress, 100))}
                    max={100}
                    className={`h-2 w-full overflow-hidden rounded-full [&::-webkit-progress-bar]:bg-muted ${
                      progress > 100
                        ? '[&::-webkit-progress-value]:bg-destructive [&::-moz-progress-bar]:bg-destructive'
                        : '[&::-webkit-progress-value]:bg-primary [&::-moz-progress-bar]:bg-primary'
                    }`}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Estimated</div>
                    <div className="text-2xl font-bold text-foreground">{estimatedHours}h</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Logged</div>
                    <div className={`text-2xl font-bold ${
                      totalLogged > estimatedHours ? 'text-destructive' : 'text-primary'
                    }`}>
                      {totalLogged.toFixed(1)}h
                    </div>
                  </div>
                </div>
                {totalLogged > estimatedHours && (
                  <div className="rounded bg-destructive/10 p-2 text-sm text-destructive">
                    ⚠️ Over estimate by {(totalLogged - estimatedHours).toFixed(1)}h
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Task Details */}
            <Card>
              <CardHeader>
                <CardTitle>Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <div className="text-muted-foreground">Status</div>
                  <Badge variant="outline" className={`mt-1 ${taskStatusBadgeClass}`}>{task.status}</Badge>
                </div>
                <div>
                  <div className="text-muted-foreground">Priority</div>
                  <Badge variant="outline" className={`mt-1 ${taskPriorityBadgeClass}`}>{task.priority}</Badge>
                </div>
                {task.complexity && (
                  <div>
                    <div className="text-muted-foreground">Complexity</div>
                    <div className="mt-1 font-medium text-foreground">{task.complexity}</div>
                  </div>
                )}
                {task.category && (
                  <div>
                    <div className="text-muted-foreground">Category</div>
                    <div className="mt-1 font-medium text-foreground">{task.category}</div>
                  </div>
                )}
                {task.phase && (
                  <div>
                    <div className="text-muted-foreground">Phase</div>
                    <div className="mt-1 font-medium text-foreground">{task.phase}</div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Artifacts */}
            {((task.verificationLinks && task.verificationLinks.length > 0) || 
              (task.externalLinks && task.externalLinks.length > 0)) && (
              <Card>
                <CardHeader>
                  <CardTitle>Artifacts</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {task.verificationLinks?.map((link, idx) => (
                    <a
                      key={idx}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <LinkIcon className="h-4 w-4" />
                      {link}
                    </a>
                  ))}
                  {task.externalLinks?.map((link, idx) => (
                    <a
                      key={idx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <LinkIcon className="h-4 w-4" />
                      {link.url}
                    </a>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}